import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';
import { useTranslation, Trans } from 'react-i18next';

import useNavigation from '../hooks/useNavigation';
import useSearch from '../hooks/useSearch';
import useWithGA from '../hooks/useWithGA';
import useStrapiPageData from '../hooks/data/useStrapiPageData';
import useGetCategoriesLinks from '../hooks/data/useGetCategoriesLinks';
import useGetMostCompared from '../hooks/data/useGetMostCompared';
import useTransformGatsbyData from '../hooks/useTransformGatsbyData';

import CONSTANTS_NUMBERS from '../constants/numbers';
import {
  PAGE_SYSTEMGUIDE,
  PAGE_CATEGORIES,
  PAGE_INDEX,
  // PAGE_CATEGORIES_COMPARE_SYSTEM,
} from '../constants/strapi';
import MetatagsForLayout from '../components/MetatagsForLayout';
import { schemaOrganization } from '../utils/schema';

import { Home } from '../ui/templates/home/Home';

import { Guide } from '../../static/icons/guide';
import { Toplist } from '../../static/icons/toplist';
import { Compare } from '../../static/icons/compare';
import { MagnifierColored } from '../../static/icons/magnifier';
import { Steps } from '../../static/icons/steps';
import { Recommendations } from '../../static/icons/recommendations';

export const query = graphql`
  query HomeQuery($pageBuildId: String!) {
    bwPageBuildData(id: { eq: $pageBuildId }) {
      data
    }
  }
`;

//
const IndexPage = ({ data, pageContext, location }) => {
  const { locale } = pageContext;

  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const { siteUrls } = bwPageBuildData.pageData;

  const { menu, footer } = useNavigation(siteUrls, location);
  const search = useSearch();
  const searchBig = useSearch(true);
  const { data: compareData } = useGetMostCompared();
  const { savePageView } = useWithGA();
  const { t } = useTranslation();
  const { categoriesLinks } = useGetCategoriesLinks();
  const [systemguidePageUrl] = useStrapiPageData(PAGE_SYSTEMGUIDE);
  const [categoriesPageUrl] = useStrapiPageData(PAGE_CATEGORIES);

  useEffect(() => {
    savePageView({
      type: 'HOME',
    });
  }, []);

  const popularCategories = [
    'CRM',
    'HR',
    'LMS',
    'ERP',
    'ECOMMERCE',
    'MANAGEMENT_SYSTEM',
    'RECRUITMENT',
    'PAYROLL',
    'PROJECT_MANAGEMENT',
  ];

  const popularLinks = categoriesLinks
    .filter((item) => popularCategories.includes(item.id))
    .map((item) => ({ name: item.name, url: item.url }));

  // const reviewItemsMain = [
  //   {
  //     name: 'formAddReview.reviewMetExpectations',
  //     rating: 4.5,
  //   },
  //   {
  //     name: 'formAddReview.reviewPriceRelation',
  //     rating: 4,
  //   },
  //   {
  //     name: 'formAddReview.reviewMobile',
  //     rating: 4.5,
  //   },
  //   {
  //     name: 'formAddReview.reviewUsablility',
  //     rating: 4,
  //   },
  //   {
  //     name: 'formAddReview.reviewSupport',
  //     rating: 3,
  //   },
  // ];

  return (
    <Home
      hero={{
        title: t('home.titleV2'),
        description: (
          <Trans
            i18nKey="home.subTitleV2"
            components={{ b: <b /> }}
            values={{ number: CONSTANTS_NUMBERS.guideRuns }}
          />
        ),
        // label: 'Vad vi gör',
        ctaSystemGuide: {
          title: t('common.systemGuide'),
          text: t('home.cta1TextV2'),
          buttonText: t('common.toSystemGuide'),
          buttonHref: systemguidePageUrl,
        },
      }}
      sections={[
        {
          sectionType: 'single',
          component: {
            type: 'siteResults',
            componentData: [
              { number: CONSTANTS_NUMBERS.reviews, text: t('home.usp1Text') },
              {
                number: CONSTANTS_NUMBERS.systems,
                text: t('home.usp2Text'),
              },
              {
                number: CONSTANTS_NUMBERS.guideRuns,
                text: t('home.usp3Text'),
              },
            ],
          },
        },
        {
          sectionType: 'single',
          component: {
            type: 'testimonials',
            title: t('home.testimonialTitle'),
            componentData: [
              {
                name: t('home.testimonial1Name'),
                role: t('home.testimonial1Work'),
                company: null,
                testimonial: t('home.testimonial1Text'),
                imgUrl:
                  'https://ik.imagekit.io/businesswith/tr:w-50,h-50,cm-pad_resize,dpr-2/testimonials/samara-johansson.jpeg',
                excludeInLocales: ['sv_SE'],
              },
              {
                name: t('home.testimonial1Name'),
                role: t('home.testimonial1Work'),
                company: null,
                testimonial: t('home.testimonial1Text'),
                imgUrl:
                  'https://ik.imagekit.io/businesswith/tr:w-50,h-50,cm-pad_resize,dpr-2/testimonials/victor-ostgren.jpeg',
                includeOnlyInLocales: ['sv_SE'],
              },
              {
                name: t('home.testimonial2Name'),
                role: t('home.testimonial2Work'),
                company: null,
                testimonial: t('home.testimonial2Text'),
                imgUrl:
                  'https://ik.imagekit.io/businesswith/tr:w-50,h-50,cm-pad_resize,dpr-2/testimonials/michael-karrberg.jpeg',
              },
              {
                name: t('home.testimonial3Name'),
                role: t('home.testimonial3Work'),
                company: null,
                testimonial: t('home.testimonial3Text'),
                imgUrl:
                  'https://ik.imagekit.io/businesswith/tr:w-50,h-50,cm-pad_resize,dpr-2/testimonials/eva-malmberg.jpeg',
              },
            ],
          },
        },
        {
          sectionType: 'dubble',
          type: 'guideSections',
          components: [
            {
              id: 'guideSection1',
              align: 'right',
              image:
                'https://ik.imagekit.io/businesswith/tr:w-658,h-840,cm-pad_resize/site/homeSectionRight.jpg',
              title: t('home.elevatorTitle'),
              actionButtonText: null,
              actionButtonHref: null,
              description: [t('home.elevatorText')],
              componentData: [
                {
                  icon: <Guide />,
                  title: t('common.systemGuide'),
                  text: t('home.elevatorSystemguide'),
                },
                {
                  icon: <Toplist />,
                  title: t('common.topLists'),
                  text: t('home.elevatorToplist'),
                },
                {
                  icon: <Compare />,
                  title: t('common.compareSystem'),
                  text: t('home.elevatorCompare'),
                },
              ],
            },
            {
              id: 'guideSection2',
              align: 'left',
              image:
                'https://ik.imagekit.io/businesswith/tr:w-658,h-840,cm-pad_resize/site/homeSectionLeft.jpg',
              title: t('home.story1Title'),
              actionButtonText: t('common.trySystemGuide'),
              actionButtonHref: systemguidePageUrl,
              description: [t('home.story1Text1'), t('home.story1Text2')],
              componentData: [
                {
                  icon: <MagnifierColored />,
                  title: t('home.story1-1'),
                  text: t('home.story1-1-text'),
                },
                {
                  icon: <Steps />,
                  title: `${t('home.story1-2-1')} - ${t(
                    'home.story1-2-2'
                  )} - ${t('home.story1-2-3')}`,
                  text: t('home.story1-2-text'),
                },
                {
                  icon: <Recommendations />,
                  title: t('home.story1-3'),
                  text: t('home.story1-3-text'),
                },
              ],
            },
          ],
        },
        {
          sectionType: 'dubble',
          type: 'reviews',
          components: [
            {
              type: 'ratings',
              titleIcon: null,
              title: null,
              componentData: [
                { text: t('formAddReview.reviewMetExpectations'), rating: 5 },
                { text: t('formAddReview.reviewPriceRelation'), rating: 4 },
                { text: t('formAddReview.reviewMobile'), rating: 4.5 },
                { text: t('formAddReview.reviewUsablility'), rating: 4 },
                { text: t('formAddReview.reviewSupport'), rating: 3 },
              ],
            },
            {
              type: 'general',
              titleIcon: 'chat',
              title: t('common.testimonialsAndReviews'),
              // label: t('common.testimonialsAndReviews'),
              componentData: {
                text1: t('home.story2Text1'),
                text2: t('home.story2Text2'),
              },
            },
          ],
        },
        {
          sectionType: 'dubble',
          type: 'compare',
          components: [
            {
              type: 'general',
              titleIcon: 'arrows',
              title: t('home.story3Title'),
              // label: 'För vem?',
              componentData: {
                text1: t('home.story3Text1'),
                text2: t('home.story3Text2'),
              },
            },
            {
              type: 'compareCards',
              titleIcon: null,
              title: null,
              componentData: compareData?.slice(0, 4) || [],
            },
          ],
        },
        {
          sectionType: 'single',
          component: {
            type: 'popularCategories',
            title: t('common.popularCategories'),
            linkUrl: categoriesPageUrl,
            componentData: popularLinks,
          },
        },
        {
          sectionType: 'single',
          type: 'digitalServices',
          component: {
            componentType: 'digitalServices',
            type: 'digitalServices',
            title: t('home.ourStoryTitle'),
            text1: t('home.ourStoryText1'),
            text2: t('home.ourStoryText2'),
            cit1: t('home.ourStoryQuote'),
          },
        },
      ]}
      t={t}
      locale={locale}
      menu={menu}
      footer={footer}
      siteUrls={siteUrls}
      location={location}
      search={search}
      searchBig={searchBig}
    />
  );
};

IndexPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  data: PropTypes.shape({
    // eslint-disable-next-line react/forbid-prop-types
    file: PropTypes.object,
  }),
};

export default IndexPage;

export function Head({ data, location, pageContext }) {
  const [pageHomeUrl, pageHomeData] = useStrapiPageData(PAGE_INDEX);
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);

  const { seo } = pageHomeData;
  const { locale } = pageContext;
  const { siteUrls } = bwPageBuildData.pageData;
  const organizationSchema = schemaOrganization();

  return (
    <>
      <MetatagsForLayout
        title={seo.metaTitle}
        description={seo.metaDescription}
        pathname={location.pathname}
        locale={locale}
        siteUrls={siteUrls}
      />
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
    </>
  );
}
